import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Avatar, Dialog, DialogContent, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import PersonIcon from '@mui/icons-material/Person';
import checkCircleImg from '../../assets/images/svgIcons/checkcircle.svg';

const primaryColor = '#1A3353';
const activeRed = '#ff3b30';

// ---- Types ----
type Step = 'basicInfo' | 'address' | 'documents' | 'serviceCatalogue';
type CatalogueSubStep = 'fieldVerification' | 'documentVerification' | 'education' | 'police';
type PendingAction = 'approve' | 'reject' | 'hold' | null;

export interface ServiceProviderData {
    currentStatus?: string;
    basicInfo: {
        firstName: string;
        lastName: string;
        mobileNo: string;
        email: string;
    };
    address: {
        addressLine1: string;
        addressLine2: string;
        country: string;
        state: string;
        city: string;
        pincode: string;
    };
    selfieUrl: string;
    documents: {
        sno: string;
        name: string;
        number: string;
        fileName: string;
        fileUrl: string;
    }[];
}

interface ServiceProviderDetailProps {
    applicant: ServiceProviderData;
    onBack: () => void;
    onApprove?: (reason: string) => void;
    onReject?: (reason: string) => void;
    onHold?: (reason: string) => void;
}

// ---- Service Catalogue is still mock data — no pricing/catalogue API exists yet ----
const mockFieldVerification = [
    { sno: '01', activity: 'Residential', coveragePostalCode: 'View', tat: '7 Days', grossPrice: 'INR 2500', tax: '18%', netPrice: 'INR 3888' },
    { sno: '02', activity: 'Office', coveragePostalCode: 'View', tat: '4 Days', grossPrice: 'INR 2500', tax: '18%', netPrice: 'INR 3888' },
];

const mockDocumentVerification = [
    { sno: '01', documentType: 'Passport', tat: '7 Days', grossPrice: '', tax: '', netPrice: '' },
    { sno: '02', documentType: 'Driving License', tat: '4 Days', grossPrice: 'INR 2500', tax: '18%', netPrice: 'INR 3888' },
];

const mockEducation = [
    { sno: '01', activity: 'Degree Certificate', rows: [{ state: 'Tamilnadu', tat: '7 Days', grossPrice: 'INR 2500', tax: '18%', netPrice: 'INR 3888' }, { state: 'Kerala', tat: '4 Days', grossPrice: 'INR 2500', tax: '18%', netPrice: 'INR 3888' }] },
    { sno: '02', activity: 'Provisional Certificate', rows: [{ state: 'Tamilnadu', tat: '6 Days', grossPrice: 'INR 2500', tax: '18%', netPrice: 'INR 3888' }] },
];

const mockPolice = [
    { sno: '01', rows: [{ state: 'Tamilnadu', tat: '7 Days', grossPrice: 'INR 2500', tax: '18%', netPrice: 'INR 3888' }, { state: 'Kerala', tat: '4 Days', grossPrice: 'INR 2500', tax: '18%', netPrice: 'INR 3888' }] },
    { sno: '02', rows: [{ state: 'Tamilnadu', tat: '6 Days', grossPrice: 'INR 2500', tax: '18%', netPrice: 'INR 3888' }] },
];

const STEPS: { key: Step; label: string }[] = [
    { key: 'basicInfo', label: 'Basic Info' },
    { key: 'address', label: 'Address Details' },
    { key: 'documents', label: 'Documents' },
    { key: 'serviceCatalogue', label: 'Service Catalogue' },
];

const CATALOGUE_SUB_STEPS: { key: CatalogueSubStep; label: string }[] = [
    { key: 'fieldVerification', label: 'Field Verification' },
    { key: 'documentVerification', label: 'Document Verification' },
    { key: 'education', label: 'Education' },
    { key: 'police', label: 'Police' },
];

function StepTabs<T extends string>({
    steps,
    currentKey,
    onSelect,
    variant = 'outer',
}: {
    steps: { key: T; label: string }[];
    currentKey: T;
    onSelect: (key: T) => void;
    variant?: 'outer' | 'inner';
}) {
    const currentIndex = steps.findIndex((s) => s.key === currentKey);
    const progressPercent = ((currentIndex + 1) / steps.length) * 100;

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                width: '100%',
                gap: variant === 'outer' ? '100px' : 0,
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: variant === 'inner' ? '#fff' : 'transparent',
                border: variant === 'inner' ? '1px solid #e0e0e0' : 'none',
                borderRadius: variant === 'inner' ? 1 : 0,
            }}
        >
            {steps.map((step, index) => {
                const isCompleted = index < currentIndex;
                const isCurrent = index === currentIndex;
                return (
                    <Box
                        key={step.key}
                        onClick={() => onSelect(step.key)}
                        sx={{
                            flex: variant === 'inner' ? 1 : 'unset',
                            textAlign: 'center',
                            cursor: 'pointer',
                            padding: variant === 'inner' ? '10px 16px' : '0 24px 10px 0',
                            display: 'flex',
                            flexDirection: variant === 'inner' ? 'row' : 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: variant === 'inner' ? '6px' : '2px',
                            backgroundColor: variant === 'inner' && isCurrent ? '#fff' : 'transparent',
                            borderRight: variant === 'inner' && index < steps.length - 1 ? '1px solid #e0e0e0' : 'none',
                        }}
                    >
                        {isCompleted && (
                            <img
                                src={checkCircleImg}
                                alt="completed"
                                style={{ width: variant === 'inner' ? 14 : 16, height: variant === 'inner' ? 14 : 16 }}
                            />
                        )}
                        {!isCompleted && variant === 'inner' && (
                            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: isCurrent ? activeRed : '#c9cbd1' }} />
                        )}
                        <Typography
                            sx={{
                                fontSize: variant === 'inner' ? 13 : 14,
                                fontWeight: isCurrent || isCompleted ? 700 : 400,
                                color: isCurrent || isCompleted ? '#000' : '#9a9da3',
                            }}
                        >
                            {step.label}
                        </Typography>
                    </Box>
                );
            })}
            {variant === 'outer' && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -1,
                        left: 0,
                        height: '2px',
                        width: `${progressPercent}%`,
                        backgroundColor: activeRed,
                        transition: 'width 0.2s ease-in-out',
                    }}
                />
            )}
        </Box>
    );
}

const ReadOnlyField = ({
    label,
    value,
    labelStyle,
    valueStyle,
}: {
    label: string;
    value: string;
    labelStyle?: React.CSSProperties;
    valueStyle?: React.CSSProperties;
}) => (
    <div className="col-auto" style={{ minWidth: 220 }}>
        <Typography sx={{ fontSize: 13, color: '#191a1b', mb: '6px', ...labelStyle }}>{label}</Typography>
        <TextField
            value={value || ''}
            size="small"
            variant="outlined"
            InputProps={{ readOnly: true }}
            sx={{
                backgroundColor: '#fff',
                minWidth: 220,
                '& .MuiOutlinedInput-input': { fontSize: 14, color: '#1A3353', ...valueStyle },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
            }}
        />
    </div>
);

function ServiceProviderDetail({ applicant, onBack, onApprove, onReject, onHold }: ServiceProviderDetailProps) {
    const [step, setStep] = useState<Step>('basicInfo');
    const [catalogueSubStep, setCatalogueSubStep] = useState<CatalogueSubStep>('fieldVerification');
    const [pendingAction, setPendingAction] = useState<PendingAction>(null);
    const [reason, setReason] = useState('');

    const stepIndex = STEPS.findIndex((s) => s.key === step);
    const subStepIndex = CATALOGUE_SUB_STEPS.findIndex((s) => s.key === catalogueSubStep);
    const isLastCatalogueSubStep = subStepIndex === CATALOGUE_SUB_STEPS.length - 1;

    const goNext = () => {
        if (step === 'serviceCatalogue') {
            if (!isLastCatalogueSubStep) {
                setCatalogueSubStep(CATALOGUE_SUB_STEPS[subStepIndex + 1].key);
            }
            return;
        }
        const nextIndex = Math.min(stepIndex + 1, STEPS.length - 1);
        setStep(STEPS[nextIndex].key);
    };

    const goBack = () => {
        if (step === 'serviceCatalogue' && subStepIndex > 0) {
            setCatalogueSubStep(CATALOGUE_SUB_STEPS[subStepIndex - 1].key);
            return;
        }
        if (stepIndex === 0) {
            onBack();
            return;
        }
        setStep(STEPS[stepIndex - 1].key);
    };

    const openActionModal = (action: PendingAction) => {
        setPendingAction(action);
    };

    const closeActionModal = () => {
        setPendingAction(null);
        setReason('');
    };

    const submitAction = () => {
        if (pendingAction === 'approve') onApprove?.(reason);
        if (pendingAction === 'reject') onReject?.(reason);
        if (pendingAction === 'hold') onHold?.(reason);
        closeActionModal();
    };

    const renderBasicInfo = () => (
        <div className="row gx-4 gy-4">
            <ReadOnlyField label="First Name" labelStyle={{ fontWeight: 600 }} value={applicant.basicInfo.firstName} valueStyle={{ color: '#191a1b' }} />
            <ReadOnlyField label="Last Name" labelStyle={{ fontWeight: 600 }} value={applicant.basicInfo.lastName} valueStyle={{ color: '#191a1b' }} />
            <ReadOnlyField label="Mobile No." labelStyle={{ fontWeight: 600 }} value={applicant.basicInfo.mobileNo} valueStyle={{ color: '#191a1b' }} />
            <ReadOnlyField label="Email address" labelStyle={{ fontWeight: 600 }} value={applicant.basicInfo.email} valueStyle={{ color: '#191a1b' }} />
        </div>
    );

    const renderAddress = () => (
        <div>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#09090a', mb: 3 }}>Current Address</Typography>
            <div className="row gx-4 gy-4">
                <div className="col-6">
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#191a1b', mb: '6px' }}>Address Line 1</Typography>
                    <TextField value={applicant.address.addressLine1} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ backgroundColor: '#fff' }} />
                </div>
                <div className="col-6">
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#191a1b', mb: '6px' }}>Address Line 2</Typography>
                    <TextField value={applicant.address.addressLine2} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ backgroundColor: '#fff' }} />
                </div>
            </div>
            <div className="row gx-4 gy-4 mt-1">
                <ReadOnlyField label="Country" labelStyle={{ fontWeight: 600 }} value={applicant.address.country} valueStyle={{ color: '#191a1b' }} />
                <ReadOnlyField label="State" labelStyle={{ fontWeight: 600 }} value={applicant.address.state} valueStyle={{ color: '#191a1b' }} />
                <ReadOnlyField label="City" labelStyle={{ fontWeight: 600 }} value={applicant.address.city} valueStyle={{ color: '#191a1b' }} />
                <ReadOnlyField label="Pincode" labelStyle={{ fontWeight: 600 }} value={applicant.address.pincode} valueStyle={{ color: '#191a1b' }} />
            </div>
        </div>
    );

    const renderDocuments = () => (
        <div>
            <Typography sx={{ textAlign: 'center', fontWeight: 700, fontSize: 14, color: '#09090a', mb: 2 }}>
                Profile Picture (Selfie)
            </Typography>
            <div className="d-flex justify-content-center mb-4">
                {applicant.selfieUrl ? (
                    <Avatar src={applicant.selfieUrl} sx={{ width: 90, height: 90 }} />
                ) : (
                    <Avatar sx={{ width: 90, height: 90, bgcolor: '#e0e0e0' }}>
                        <PersonIcon sx={{ fontSize: 40, color: '#9a9da3' }} />
                    </Avatar>
                )}
            </div>
            <Typography sx={{ textAlign: 'center', fontWeight: 700, fontSize: 14, color: '#09090a', mb: 2 }}>
                Uploaded Documents
            </Typography>
            <table className="w-100" style={{ borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ backgroundColor: '#fff' }}>
                        <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>S.No</th>
                        <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Document Name</th>
                        <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Document No.</th>
                        <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Document</th>
                    </tr>
                </thead>
                <tbody>
                    {applicant.documents.length ? (
                        applicant.documents.map((doc, i) => (
                            <tr key={doc.sno} style={{ backgroundColor: i === 0 ? '#fdece9' : '#fff' }}>
                                <td style={{ padding: 10, fontSize: 13 }}>{doc.sno}</td>
                                <td style={{ padding: 10, fontSize: 13 }}>{doc.name}</td>
                                <td style={{ padding: 10, fontSize: 13 }}>{doc.number}</td>
                                <td style={{ padding: 10, fontSize: 13 }}>
                                    {doc.fileName || '-'}
                                    {doc.fileUrl && (
                                        <IconButton size="small" onClick={() => window.open(doc.fileUrl, '_blank')}>
                                            <VisibilityIcon sx={{ fontSize: 16, color: '#5a5d63' }} />
                                        </IconButton>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={4} style={{ padding: 10, fontSize: 13, textAlign: 'center' }}>No documents found</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    const renderFieldVerification = () => (
        <table className="w-100" style={{ borderCollapse: 'collapse' }}>
            <thead>
                <tr>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>S.No</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Activity</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Coverage Postal Code</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>TAT</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Gross Price</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Tax (%)</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Net Price</th>
                </tr>
            </thead>
            <tbody>
                {mockFieldVerification.map((r, i) => (
                    <tr key={r.sno} style={{ backgroundColor: i === 0 ? '#fdece9' : '#fff' }}>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.sno}</td>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.activity}</td>
                        <td style={{ padding: 10, fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}>{r.coveragePostalCode}</td>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.tat}</td>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.grossPrice}</td>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.tax}</td>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.netPrice}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderDocumentVerification = () => (
        <table className="w-100" style={{ borderCollapse: 'collapse' }}>
            <thead>
                <tr>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>S.No</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Document Type</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>TAT</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Gross Price</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Tax (%)</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Net Price</th>
                </tr>
            </thead>
            <tbody>
                {mockDocumentVerification.map((r, i) => (
                    <tr key={r.sno} style={{ backgroundColor: i === 0 ? '#fdece9' : '#fff' }}>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.sno}</td>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.documentType}</td>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.tat}</td>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.grossPrice}</td>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.tax}</td>
                        <td style={{ padding: 10, fontSize: 13 }}>{r.netPrice}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderMultiRowTable = (
        data: { sno: string; activity?: string; rows: { state: string; tat: string; grossPrice: string; tax: string; netPrice: string }[] }[],
        showActivityColumn: boolean
    ) => (
        <table className="w-100" style={{ borderCollapse: 'collapse' }}>
            <thead>
                <tr>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>S.No</th>
                    {showActivityColumn && <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Activity</th>}
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>State</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>TAT</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Gross Price</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Tax (%)</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 13, color: '#353535' }}>Net Price</th>
                </tr>
            </thead>
            <tbody>
                {data.map((group, groupIndex) => (
                    group.rows.map((r, rowIndex) => (
                        <tr key={`${group.sno}-${rowIndex}`} style={{ backgroundColor: groupIndex === 0 ? '#fdece9' : '#fff' }}>
                            {rowIndex === 0 && (
                                <td style={{ padding: 10, fontSize: 13 }} rowSpan={group.rows.length}>{group.sno}</td>
                            )}
                            {showActivityColumn && rowIndex === 0 && (
                                <td style={{ padding: 10, fontSize: 13 }} rowSpan={group.rows.length}>{group.activity}</td>
                            )}
                            <td style={{ padding: 10, fontSize: 13 }}>{r.state}</td>
                            <td style={{ padding: 10, fontSize: 13 }}>{r.tat}</td>
                            <td style={{ padding: 10, fontSize: 13 }}>{r.grossPrice}</td>
                            <td style={{ padding: 10, fontSize: 13 }}>{r.tax}</td>
                            <td style={{ padding: 10, fontSize: 13 }}>{r.netPrice}</td>
                        </tr>
                    ))
                ))}
            </tbody>
        </table>
    );

    const renderServiceCatalogue = () => (
        <div>
            <Box sx={{ mb: 3 }}>
                <StepTabs
                    steps={CATALOGUE_SUB_STEPS}
                    currentKey={catalogueSubStep}
                    onSelect={setCatalogueSubStep}
                    variant="inner"
                />
            </Box>
            {catalogueSubStep === 'fieldVerification' && renderFieldVerification()}
            {catalogueSubStep === 'documentVerification' && renderDocumentVerification()}
            {catalogueSubStep === 'education' && renderMultiRowTable(mockEducation, true)}
            {catalogueSubStep === 'police' && renderMultiRowTable(mockPolice, false)}
        </div>
    );

    const isOnPoliceStep = step === 'serviceCatalogue' && catalogueSubStep === 'police';

    return (
        <div className="p-3" style={{ backgroundColor: 'white' }}>
            <div className="d-flex align-items-center mb-3" style={{ cursor: 'pointer' }} onClick={onBack}>
                <ArrowBackIcon sx={{ fontSize: 16, color: primaryColor, mr: 1 }} />
                <Typography sx={{ fontWeight: 600, color: primaryColor, fontSize: 14 }}>Back</Typography>
            </div>

            <StepTabs steps={STEPS} currentKey={step} onSelect={setStep} variant="outer" />

            <div className="content-area py-4" style={{ minHeight: 320 }}>
                {step === 'basicInfo' && renderBasicInfo()}
                {step === 'address' && renderAddress()}
                {step === 'documents' && renderDocuments()}
                {step === 'serviceCatalogue' && renderServiceCatalogue()}
            </div>

            <div className="d-flex justify-content-end gap-2 pt-3" style={{ borderTop: '1px solid #e0e0e0' }}>
                {step !== 'basicInfo' && (
                    <Button
                        variant="outlined"
                        onClick={goBack}
                        sx={{ textTransform: 'none', borderColor: '#c9cbd1', color: primaryColor, minWidth: 90 }}
                    >
                        Back
                    </Button>
                )}

                {isOnPoliceStep ? (
                    <>
                        <Button
                            variant="outlined"
                            onClick={() => openActionModal('reject')}
                            sx={{ textTransform: 'none', borderColor: activeRed, color: activeRed, minWidth: 90 }}
                        >
                            Reject
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => openActionModal('hold')}
                            sx={{ textTransform: 'none', backgroundColor: '#e0a800', minWidth: 90, boxShadow: 'none' }}
                        >
                            On-hold
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => openActionModal('approve')}
                            sx={{ textTransform: 'none', backgroundColor: '#2e7d32', minWidth: 90, boxShadow: 'none' }}
                        >
                            Approve
                        </Button>
                    </>
                ) : (
                    <Button
                        variant="contained"
                        onClick={goNext}
                        sx={{ textTransform: 'none', backgroundColor: activeRed, minWidth: 90, boxShadow: 'none' }}
                    >
                        Next
                    </Button>
                )}
            </div>

            {/* Approve / Reject / Hold reason modal */}
            <Dialog open={pendingAction !== null} onClose={closeActionModal} maxWidth="xs" fullWidth>
                <DialogContent sx={{ p: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: primaryColor, mb: 2 }}>
                        Reason for {pendingAction === 'reject' ? 'Rejection' : pendingAction === 'hold' ? 'Hold' : 'Approval'}
                    </Typography>
                    <TextField
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        multiline
                        minRows={6}
                        fullWidth
                        placeholder="Type reason here"
                        sx={{ backgroundColor: '#f5f5f5' }}
                    />
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button
                            variant="outlined"
                            onClick={closeActionModal}
                            sx={{ textTransform: 'none', borderColor: '#c9cbd1', color: primaryColor, minWidth: 90 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={submitAction}
                            sx={{ textTransform: 'none', backgroundColor: activeRed, minWidth: 90, boxShadow: 'none' }}
                        >
                            Submit
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default ServiceProviderDetail;
